import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, LayoutGrid, Search } from "lucide-react";
import { APP_LIST } from "@/data/apps";
import {
  CATEGORY_ACCENTS,
  UTILITIES,
  UTILITIES_BY_CATEGORY,
  utilityById,
} from "@/data/utilities";
import { UtilityView } from "@/components/apps/utilities/engines";
import { useHive } from "@/store/useHive";
import { externalAppUrl } from "@/lib/appLinks";
import { AppHeader, Card, SectionTitle } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";

/** The real app directory — desktop apps plus the 139 internal utilities.
 *  Utilities open in-place (master → detail) inside this window. */
export function LauncherApp() {
  const openApp = useHive((s) => s.openApp);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [activeUtilId, setActiveUtilId] = useState<string | null>(null);

  const activeUtil = activeUtilId ? utilityById(activeUtilId) : undefined;

  const needle = q.trim().toLowerCase();
  const groups: { label: string; group: "product" | "system" }[] = [
    { label: "Products", group: "product" },
    { label: "System", group: "system" },
  ];

  const utilityGroups = useMemo(
    () =>
      UTILITIES_BY_CATEGORY.map(({ category: cat, items }) => ({
        category: cat,
        items: items.filter(
          (u) =>
            (!category || category === cat) &&
            (!needle ||
              u.name.toLowerCase().includes(needle) ||
              u.tagline.toLowerCase().includes(needle) ||
              cat.toLowerCase().includes(needle))
        ),
      })).filter((g) => g.items.length > 0),
    [needle, category]
  );
  const utilityMatches = utilityGroups.reduce((n, g) => n + g.items.length, 0);

  /* ── Detail view: one utility, opened in place ── */
  if (activeUtil) {
    const accent = CATEGORY_ACCENTS[activeUtil.category] ?? "#eab308";
    return (
      <div className="min-h-full">
        <div className="flex items-center gap-3 border-b border-app px-6 py-4">
          <button
            onClick={() => setActiveUtilId(null)}
            className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2 px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
          >
            <ArrowLeft size={13} /> All Apps
          </button>
          <div
            className="grid h-10 w-10 place-items-center rounded-xl text-white shadow-lg"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}
          >
            <activeUtil.icon size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold leading-tight text-app">{activeUtil.name}</div>
            <div className="text-xs text-app-2">
              {activeUtil.category} · {activeUtil.tagline}
            </div>
          </div>
        </div>
        <div className="p-6">
          <UtilityView def={activeUtil} />
        </div>
      </div>
    );
  }

  /* ── Directory view ── */
  return (
    <div className="min-h-full">
      <AppHeader
        icon={LayoutGrid}
        title="All Apps"
        subtitle={`${APP_LIST.length - 1} desktop apps · ${UTILITIES.length} internal utilities`}
        accent="#eab308"
        actions={
          <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2 px-2.5 py-1.5">
            <Search size={13} className="text-app-2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search apps & utilities…"
              className="w-48 bg-transparent text-xs text-app outline-none placeholder:text-app-2"
            />
          </div>
        }
      />

      <div className="space-y-6 p-6">
        {groups.map(({ label, group }) => {
          const apps = APP_LIST.filter(
            (a) =>
              a.id !== "launcher" && // no self-referential card
              a.group === group &&
              (!needle || a.name.toLowerCase().includes(needle))
          );
          if (apps.length === 0) return null;
          return (
            <div key={group}>
              <SectionTitle>{label}</SectionTitle>
              <div className="grid grid-cols-3 gap-3">
                {apps.map((a, i) => {
                  const external = !!externalAppUrl(a.id);
                  return (
                    <motion.button
                      key={a.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      whileHover={{ y: -2 }}
                      onClick={() => openApp(a.id)}
                      className="text-left"
                    >
                      <Card className="flex items-center gap-3 p-4 transition hover:border-app-strong">
                        <div
                          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white shadow-lg"
                          style={{ background: `linear-gradient(135deg, ${a.accent}, ${a.accent}aa)` }}
                        >
                          <a.icon size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 text-sm font-semibold text-app">
                            {a.name}
                            {external && <ArrowUpRight size={12} className="text-app-2" />}
                          </div>
                          <div className="truncate text-xs text-app-2">{a.tagline}</div>
                        </div>
                      </Card>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* ── Internal utilities ── */}
        <div>
          <SectionTitle
            right={
              <span className="text-[11px] text-app-2">
                {utilityMatches} of {UTILITIES.length}
              </span>
            }
          >
            Utilities
          </SectionTitle>
          <div className="mb-4 flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategory(null)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                category === null
                  ? "border-app-strong bg-surface-2 text-app"
                  : "border-app text-app-2 hover:border-app-strong hover:text-app-1"
              )}
            >
              All
            </button>
            {UTILITIES_BY_CATEGORY.map(({ category: cat, items }) => (
              <button
                key={cat}
                onClick={() => setCategory(category === cat ? null : cat)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                  category === cat
                    ? "border-app-strong bg-surface-2 text-app"
                    : "border-app text-app-2 hover:border-app-strong hover:text-app-1"
                )}
              >
                <span
                  className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle"
                  style={{ background: CATEGORY_ACCENTS[cat] }}
                />
                {cat} · {items.length}
              </button>
            ))}
          </div>

          {utilityMatches === 0 ? (
            <Card className="p-8 text-center">
              <div className="text-sm font-semibold text-app">No utilities match</div>
              <p className="mt-1 text-xs text-app-2">
                Try a different search term or clear the category filter.
              </p>
            </Card>
          ) : (
            <div className="space-y-5">
              {utilityGroups.map(({ category: cat, items }) => (
                <div key={cat}>
                  <div className="mb-2 flex items-center gap-2">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: CATEGORY_ACCENTS[cat] }}
                    />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-app-2">
                      {cat}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {items.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setActiveUtilId(u.id)}
                        className="text-left"
                      >
                        <Card className="flex items-center gap-2.5 p-3 transition hover:border-app-strong">
                          <div
                            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                            style={{
                              background: `${CATEGORY_ACCENTS[cat]}1a`,
                              color: CATEGORY_ACCENTS[cat],
                            }}
                          >
                            <u.icon size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-semibold text-app">{u.name}</div>
                            <div className="truncate text-[11px] text-app-2">{u.tagline}</div>
                          </div>
                        </Card>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
